import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SpecialHours, StoreSchedule } from '@generated/tcea/models';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';

/**
 *  Intercepts 'hours' fields in menu and sets the startTime and endTime to ISO format
 *  (adds ':00.000') when sending data to server, and removes the extra :00.000 from the
 *  server data when requesting data from server
 */
@Injectable()
export class TranslateIsoHoursInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let request = req;
    const url = req.url;
    const isDistributionRuleRequest = /distribution-rules/.test(req.url);
    /** Menu hours groups API expects `HH:mm`, not ISO (`HH:mm:ss.SSS`). */
    const isMenuHoursGroupRequest = /\/menus\/[^/]+\/hours(?:\/[^/?]+)?$/.test(url);
    if (
      !isDistributionRuleRequest &&
      !isMenuHoursGroupRequest &&
      (req.method === 'PUT' || req.method === 'POST') &&
      req.body &&
      (req.body?.hours ||
        req.body.schedule ||
        req.body.scheduledHours ||
        req.body.dayParts ||
        req.body.attributes?.schedule ||
        req.body.properties?.zoneHours)
    ) {
      const body = this.fixRequestHours(req.body);
      request = req.clone({ body });
    }

    return next.handle(request).pipe(
      filter((event) => event instanceof HttpResponse),
      map((event: HttpResponse<any>) => {
        // only deal with api requests
        const regex = /\/tce\-experience\-admin\//;
        if (!isDistributionRuleRequest && regex.test(url)) {
          const body = event.body;
          this.fixResponseHours(body);
        }
        return event.clone({ body: event.body });
      }),
    );
  }

  /**
   * Looks for _embedded.menus or hours on given body to fix
   *
   * @param body - body to check
   */
  private fixResponseHours(body: any) {
    if (body?._embedded) {
      const menus = body._embedded.menus;
      if (menus) {
        menus.forEach((m) => this.fixHours(m.hours, this.removeIsoSecMs));
      }
    } else if (body?.schedule) {
      this.fixSchedule(body.schedule, this.removeIsoSecMs);
    } else if (body?.features) {
      body.features.forEach((zh) =>
        this.fixSchedule([zh?.properties?.zoneHours], this.removeIsoSecMs),
      );
    } else {
      this.fixHours(body?.hours, this.removeIsoSecMs);
      this.fixHours(body?.scheduledHours, this.removeIsoSecMs);
      this.fixDayParts(body?.dayParts, this.removeIsoSecMs);
      this.fixHours(body?.dayOverrides, this.removeIsoSecMs);
    }
  }

  /**
   * Looks for 'hours' attribute on body and fixes startTime and endTime
   * on the hours with the given function
   *
   * @param body - body to fix
   * @param fnFix - function to run on startTime/endTime
   */
  private fixHours(hoursMap: any, fnFix: any, fnFixEnd = fnFix) {
    if (hoursMap) {
      for (const key in hoursMap) {
        if (hoursMap.hasOwnProperty(key)) {
          const hrList = hoursMap[key];
          if (hrList && hrList.length > 0) {
            hrList.forEach((h) => {
              if (h.startTime) {
                h.startTime = fnFix(h.startTime);
              }
              if (h.endTime) {
                h.endTime = fnFixEnd(h.endTime);
              }
            });
          }
        }
      }
    }
  }

  /**
   * Looks for 'hours' attribute on body and fixes startTime and endTime
   * on the hours with the given function
   *
   * @param body - body to fix
   * @param fnFix - function to run on startTime/endTime
   */
  private fixDayParts(dayPartsMap: any, fnFix: any, fnFixEnd = fnFix) {
    let hoursMap = {};
    if (dayPartsMap) {
      for (const key in dayPartsMap) {
        hoursMap = dayPartsMap[key].hours;
        if (hoursMap) {
          for (const key in hoursMap) {
            if (hoursMap.hasOwnProperty(key)) {
              const hrList = hoursMap[key];
              if (hrList && hrList.length > 0) {
                hrList.forEach((h) => {
                  if (h.startTime) {
                    h.startTime = fnFix(h.startTime);
                  }
                  if (h.endTime) {
                    h.endTime = fnFixEnd(h.endTime);
                  }
                });
              }
            }
          }
        }
      }
    }
  }

  /**
   * Fixes startTime and endTime on all 'operatingHours' for the given Special Hours array
   * with the given function
   *
   * @param specialHours - Special Hours array to fix
   * @param fnFix - function to run on startTime/endTime
   */
  private fixSpecialHours(specialHours: SpecialHours[], fnFix: any, fnFixEnd = fnFix) {
    if (specialHours) {
      for (const item of specialHours) {
        const operatingHours = item.operatingHours;
        if (operatingHours) {
          for (const h of operatingHours) {
            if (h.startTime) {
              h.startTime = fnFix(h.startTime);
            }
            if (h.endTime) {
              h.endTime = fnFixEnd(h.endTime);
            }
          }
        }
      }
    }
  }

  /**
   * Returns a new object that fixes the given request body by correcting ISO time fields
   *
   * @param body - request body to fix
   */
  private fixRequestHours(body: any): any {
    // clone body so we aren't manipulating the original object
    const clone = DeepCloneService.clone(body);
    this.fixHours(clone?.hours, this.makeIsoTime, this.makeIsoEndTime);
    this.fixHours(clone?.scheduledHours, this.makeIsoTime, this.makeIsoEndTime);
    this.fixSchedule(clone?.schedule, this.makeIsoTime, this.makeIsoEndTime);
    this.fixSchedule(clone?.attributes?.schedule, this.makeIsoTime, this.makeIsoEndTime);
    this.fixSchedule([clone?.properties?.zoneHours], this.makeIsoTime, this.makeIsoEndTime);
    this.fixDayParts(clone?.dayParts, this.makeIsoTime, this.makeIsoEndTime);
    return clone;
  }

  /**
   * Fixes the given schedule by correcting ISO time fields within the object
   *
   * @param schedule - StoreSchedule to fix
   * @param fnFix - function to run on startTime/endTime
   */
  private fixSchedule(schedule: StoreSchedule[], fnFix: any, fnFixEnd = fnFix) {
    if (schedule) {
      for (const item of schedule) {
        this.fixHours(item?.hours, fnFix, fnFixEnd);
        this.fixHours(item?.scheduledHours, fnFix, fnFixEnd);
        this.fixSpecialHours(item?.specialHours, fnFix, fnFixEnd);
        this.fixSpecialHours(item?.scheduledSpecialHours, fnFix, fnFixEnd);
      }
    }
  }

  /**
   * Adds :00.000 to a given time string if it is not iso time already. Prepends '0' if
   * the hour is only 1 digit.
   *
   * @param time - time to fix
   */
  private makeIsoTime(time: string): string {
    const ENDS_WITH_ISO_REGEX = /[0-9]{2}.[0-9]{3}$/g;
    const suffix = ':00.000';
    let prefix = '';
    if (time && !time.match(ENDS_WITH_ISO_REGEX)) {
      if (time.split(':')[0].length === 1) {
        prefix = '0';
      }
      return prefix + time + suffix;
    } else {
      return time;
    }
  }

  /**
   * Adds :59.999 to a given time string if it is not iso time already. Prepends '0' if
   * the hour is only 1 digit. Used for end times to include the last millisecond of the
   * entered minute.
   *
   * @param time - time to fix
   */
  private makeIsoEndTime(time: string): string {
    const ENDS_WITH_ISO_REGEX = /[0-9]{2}.[0-9]{3}$/g;
    const suffix = ':59.999';
    let prefix = '';
    if (time && !time.match(ENDS_WITH_ISO_REGEX)) {
      if (time.split(':')[0].length === 1) {
        prefix = '0';
      }
      return prefix + time + suffix;
    } else {
      return time;
    }
  }

  /**
   * Removes iso time (:00.000 or any other s and ms) from a given time string and removes leading 0 from hours
   *
   * @param time - time string to fix
   */
  private removeIsoSecMs(time: string): string {
    const ENDS_WITH_ISO_REGEX = /:[0-9]{2}.[0-9]{3}$/g;
    let result = time;
    if (time) {
      result = time.replace(ENDS_WITH_ISO_REGEX, '');
      if (result.startsWith('0')) {
        result = result.substring(1);
      }
    }
    return result;
  }
}
